import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import './index.css'
import '@calcom/atoms/globals.min.css'
import { AuthProvider } from './hooks/useAuth'
import { EnhancedErrorBoundary } from './components/error/EnhancedErrorBoundary'
import { FEATURES } from './config/features'

// Initialiser Sentry
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' ou 'production'
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% des sessions
    replaysOnErrorSampleRate: 1.0, // 100% des sessions avec erreur
  });
}

// Create a client with optimized configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 secondes (plus agressif)
      gcTime: 1000 * 60 * 5, // 5 minutes en cache
      retry: 2, // 2 tentatives au lieu de 1
      
      // Refetch automatique (contrôlé par feature flags)
      refetchOnWindowFocus: FEATURES.REACT_QUERY_REFETCH,
      refetchOnReconnect: FEATURES.REACT_QUERY_REFETCH,
      
      // Pas de polling automatique global (géré par hook)
      refetchInterval: false,
    },
    mutations: {
      retry: 1,
    },
  },
})

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <EnhancedErrorBoundary componentName="App" showContactSupport={true}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
        {/* React Query Devtools pour debugging */}
        {FEATURES.SHOW_QUERY_DEVTOOLS && (
          <ReactQueryDevtools 
            initialIsOpen={false}
            position="bottom"
          />
        )}
      </QueryClientProvider>
    </EnhancedErrorBoundary>
  </StrictMode>
);
